import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Box, Button, Tab, Tabs, Tooltip, Typography} from '@mui/material';
import DashboardShell from '../components/DashboardShell';
import CpcLoader from '../components/CpcLoader';
import {DashboardProvider, useDashboard} from '../context/DashboardContext';
import {
    AMBER,
    BREAKDOWN,
    CORAL,
    EMERALD,
    NATIONAL_SCORE,
    PURPLE,
    STAKEHOLDERS,
    TARGETS,
    tier,
    tierByLabel,
} from '../data';
import {getCpcGovOverviewService, isCpcGovLoggedIn} from '../../../services/cpcGov.service';

/** "CPCI Stakeholder 3" -> 3, "CPCI Target 07" -> 7. */
const trailingNumber = (id) => parseInt(String(id || '').match(/(\d+)\s*$/)?.[1], 10) || 0;

/**
 * API-01 -> the shapes these tabs already render.
 *
 * Colours, the toolkit blurbs and the status meanings stay with the bundled
 * sample: they are design copy, not country data, and the CPC side does not
 * ship them. Everything measurable — counts, tier, confidence — comes from the
 * API.
 */
const adaptOverview = (d) => {
    const counts = (totals) => ({
        met: totals?.Met || 0,
        partial: totals?.Partially || 0,
        notMet: totals?.['Not Met'] || 0,
        total: totals?.total || 0,
    });

    return {
        nationalTier: tierByLabel(d.cpci?.maturity),
        confidence: d.cpci?.confidence || '',
        breakdown: [
            {label: 'Met', color: EMERALD, n: d.cpci?.totals?.Met || 0, meaning: BREAKDOWN[0]?.meaning},
            {label: 'Partially', color: AMBER, n: d.cpci?.totals?.Partially || 0, meaning: BREAKDOWN[1]?.meaning},
            {label: 'Not Met', color: CORAL, n: d.cpci?.totals?.['Not Met'] || 0, meaning: BREAKDOWN[2]?.meaning},
        ],
        stakeholders: (d.stakeholder || []).map((s) => {
            const id = trailingNumber(s.stakeholder_id);
            const sample = STAKEHOLDERS.find((x) => x.id === id) || {};
            const c = counts(s.totals);
            return {
                id,
                name: s.name || sample.name || s.stakeholder_id,
                color: sample.color,
                toolkit: sample.toolkit || '',
                tierStyle: tierByLabel(s.maturity),
                confidence: s.confidence || '',
                met: c.met, partial: c.partial, notMet: c.notMet, indicators: c.total,
            };
        }),
        targets: (d.target || []).map((t) => {
            const n = trailingNumber(t.target_id);
            const sample = TARGETS.find((x) => x.n === n) || {};
            const c = counts(t.totals);
            return {
                n,
                name: t.name || sample.name || t.target_id,
                s: sample.s,
                tierStyle: tierByLabel(t.maturity),
                confidence: t.confidence || '',
                met: c.met, partial: c.partial, notMet: c.notMet, indicators: c.total,
            };
        }),
    };
};
const TAB_PATHS = [
    '/dq_pro/cpc-government-dashboard/overview/executive-summary',
    '/dq_pro/cpc-government-dashboard/overview/stakeholder-scores',
    '/dq_pro/cpc-government-dashboard/overview/target-scores',
];

const TAB_LABELS = ['Executive Summary', 'Stakeholder Scores', 'Target Scores'];

const C = {
    // navy/navyDeep/white stay literal — besides body/heading text, they're
    // also used as solid brand surfaces (stakeholder-card headers, filled CTA
    // buttons, white-on-navy labels) that must stay put in both themes. Plain
    // text/background roles use the dedicated keys below instead, which flip
    // via --cpc-* (see darkMode.css) while these stay untouched.
    navy: '#003C69',
    navyDeep: '#003D69',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    white: '#FFFFFF',
    mist: 'var(--cpc-surface-subtle, #F5F8FB)',
    muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const cardSx = {background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))', boxShadow: '0 8px 28px rgba(0,40,85,.06)', mb: 5};

const ctaSx = (bg, border, color, hoverBg) => ({
    border: `2px solid ${border}`,
    background: bg,
    color,
    fontFamily: 'inherit',
    fontSize: 16,
    fontWeight: 700,
    padding: '13px 22px',
    borderRadius: 0,
    textTransform: 'none',
    ...(hoverBg && {'&:hover': {background: hoverBg}}),
});

const sectionH2Sx = {fontSize: 32, fontWeight: 700, color: C.text, margin: '0 0 14px'};
const sectionPSx = {fontSize: 19, lineHeight: 1.55, color: C.muted, margin: '0 0 10px'};

const NationalOverviewPage = () => {
    const history = useHistory();
    const location = useLocation();
    const {requestSent, setRequestSent} = useDashboard();

    const activeTab = Math.max(TAB_PATHS.indexOf(location.pathname), 0);

    useEffect(() => {
        document.title = `National Overview · ${TAB_LABELS[activeTab]}`;
    }, [activeTab]);

    const [notice, setNotice] = useState('');
    useEffect(() => setNotice(''), [activeTab]);

    // Signed in -> this reviewer's own country (API-01). Signed out -> the
    // bundled sample, so the screen stays reviewable before accounts exist.
    const authed = isCpcGovLoggedIn();
    const [api, setApi] = useState(null);
    // Signed in, render nothing until the real figures arrive. Showing the sample
    // first made the page flash a different tier and different counts before
    // snapping to the country's own — a reviewer must never see numbers that
    // are not theirs, even for a moment.
    const [loading, setLoading] = useState(isCpcGovLoggedIn());

    useEffect(() => {
        if (!authed) return undefined;
        let alive = true;
        getCpcGovOverviewService()
            .then((res) => {
                if (!alive) return;
                if (res?.data) setApi(adaptOverview(res.data));
                setLoading(false);
            })
            // Failed: fall back to the sample rather than an empty page.
            .catch(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [authed]);

    // No numeric score anywhere on the real path: the tier is what the CPC side
    // publishes, and tier(NATIONAL_SCORE) only ever runs for the offline sample.
    const nationalTier = api ? api.nationalTier : tier(NATIONAL_SCORE);
    const breakdown = api ? api.breakdown : BREAKDOWN;
    const stakeholderCards = useMemo(() => (api ? api.stakeholders : STAKEHOLDERS.map((s) => ({
        ...s, tierStyle: tier(s.score), confidence: '',
    }))), [api]);
    const targetCards = useMemo(() => (api ? api.targets : TARGETS.map((t) => ({
        ...t, tierStyle: tier(t.score), confidence: '',
    }))), [api]);
    const totalBreakdown = breakdown.reduce((a, b) => a + b.n, 0);

    const handleRequest = () => {
        // TODO: wire up the actual email/request flow later.
        setRequestSent(true);
    };

    return (
        <DashboardShell active="overview">
            <Box
                sx={{
                    padding: '40px 56px 80px',
                    '@media (max-width: 1439px)': {paddingLeft: '40px', paddingRight: '28px'},
                    '@media (max-width: 720px)': {padding: '24px 20px 56px'},
                }}
            >
                <Typography sx={{fontSize: 14, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.25}}>
                    National Overview
                </Typography>

                <Tabs
                    value={activeTab}
                    onChange={(_, v) => history.push(TAB_PATHS[v])}
                    TabIndicatorProps={{style: {backgroundColor: C.cyan, height: 3}}}
                    sx={{
                        borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                        mb: 4,
                        minHeight: 'auto',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontFamily: 'inherit',
                            fontSize: 18,
                            fontWeight: 700,
                            color: C.muted,
                            minWidth: 'auto',
                            minHeight: 'auto',
                            padding: '12px 22px',
                            '&:hover': {color: C.text},
                        },
                        '& .Mui-selected': {color: `${C.text} !important`},
                    }}
                >
                    {TAB_LABELS.map((label) => (
                        <Tab key={label} label={label}/>
                    ))}
                </Tabs>

                {loading && (
                    <Box sx={cardSx}>
                        <CpcLoader label="Loading your country’s figures…"/>
                    </Box>
                )}

                {!loading && activeTab === 0 && (
                    <Box sx={cardSx}>
                        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}}}>
                            <Box
                                sx={{
                                    flex: '1.7 1 0%',
                                    padding: '32px 36px',
                                    borderRight: {md: '1px solid var(--cpc-border, rgba(0,60,105,.1))'},
                                    borderBottom: {xs: '1px solid var(--cpc-border, rgba(0,60,105,.1))', md: 'none'},
                                }}
                            >
                                <Typography component="h2" sx={{margin: '0 0 18px', fontSize: 28, fontWeight: 700, color: C.text}}>
                                    Child Protection in Cyberspace (CPC) Index — National Baseline
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25}}>
                                    <Box component="span" sx={{fontSize: 15, fontWeight: 700, color: C.text, background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '9px 14px'}}>
                                        Government Checklist: <b>85</b> indicators
                                    </Box>
                                    <Box component="span" sx={{fontSize: 15, fontWeight: 700, color: C.text, background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '9px 14px'}}>
                                        Children&rsquo;s Assessment: <b>15</b> indicators
                                    </Box>
                                    <Box component="span" sx={{fontSize: 15, fontWeight: 700, color: C.text, background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '9px 14px'}}>
                                        Assessment cycle: <b>2025–2026</b>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{flex: '1 1 0%', padding: '32px 36px', background: C.mist, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.5}}>
                                    National Standing
                                </Typography>
                                <Box component="span" sx={{display: 'inline-block', alignSelf: 'flex-start', fontSize: 17, fontWeight: 700, padding: '8px 14px', mb: 1.25, background: nationalTier.bg, color: nationalTier.fg}}>
                                    {nationalTier.label}
                                </Box>
                                <Typography sx={{fontSize: 16, fontWeight: 600, color: C.text, mb: 1.25}}>Global {nationalTier.pct}</Typography>
                                <Typography sx={{fontSize: 17, lineHeight: 1.5, color: C.muted, margin: 0}}>{nationalTier.meaning}</Typography>
                                {/* Confidence is half of VZ-OVW-01 in the CPC schema — it says how
                                    much evidence the tier rests on, so showing the tier without it
                                    overstates certainty. Only rendered on the real data path. */}
                                {api?.confidence && (
                                    <Typography sx={{fontSize: 15, color: C.muted, margin: '14px 0 0'}}>
                                        Evidence confidence:{' '}
                                        <Box component="span" sx={{fontWeight: 700, color: C.text}}>{api.confidence}</Box>
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{padding: '28px 36px 32px', borderTop: '1px solid var(--cpc-border, rgba(0,60,105,.1))'}}>
                            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 0.5}}>
                                <Typography sx={{fontSize: 18, fontWeight: 700, color: C.text}}>Checklist status across 100 indicators</Typography>
                            </Box>
                            <Typography sx={{fontSize: 16, color: C.muted, margin: '0 0 18px'}}>Every indicator is assessed as Met, Partially, or Not Met</Typography>

                            <Box sx={{display: 'flex', height: 14, overflow: 'hidden', mb: 2.75, border: '1px solid var(--cpc-border, rgba(0,60,105,.08))'}}>
                                {breakdown.map((b) => (
                                    <Box key={b.label} title={`${b.label}: ${b.n}`} sx={{width: `${(b.n / totalBreakdown) * 100}%`, background: b.color}}/>
                                ))}
                            </Box>

                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.75, mb: 3}}>
                                {breakdown.map((b) => (
                                    <Box key={b.label} sx={{border: '1px solid var(--cpc-border, rgba(0,60,105,.1))', padding: '16px 18px'}}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.75}}>
                                            <Box sx={{width: 10, height: 10, flex: 'none', background: b.color}}/>
                                            <Typography sx={{fontSize: 16, fontWeight: 700, color: C.text}}>{b.label}</Typography>
                                        </Box>
                                        <Typography sx={{fontSize: 30, fontWeight: 700, margin: '4px 0', color: b.color}}>{b.n}</Typography>
                                        <Typography sx={{fontSize: 15, color: C.muted, margin: 0, lineHeight: 1.4}}>{b.meaning}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5}}>
                                <Tooltip title="Review and act on all 85 checklist indicators" arrow>
                                    <Button onClick={() => history.push('/dq_pro/cpc-government-dashboard/explorer')} sx={ctaSx(C.cyan, C.cyan, C.white, C.cyanDeep)}>
                                        Open Checklist Explorer
                                    </Button>
                                </Tooltip>
                                <Tooltip title="See how much of the checklist has been reviewed" arrow>
                                    <Button onClick={() => history.push('/dq_pro/cpc-government-dashboard/progress')} sx={ctaSx(C.surface, 'var(--cpc-border, rgba(0,60,105,.25))', C.text)}>
                                        Review Progress
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Ask the CPC Index team for this country's Children's Assessment result" arrow>
                                    <Button onClick={handleRequest} sx={ctaSx(C.surface, 'var(--cpc-border, rgba(0,60,105,.25))', C.text)}>
                                        Request Children&rsquo;s Assessment
                                    </Button>
                                </Tooltip>
                            </Box>
                            {requestSent && (
                                <Typography sx={{mt: 1.75, fontSize: 16, fontWeight: 700, color: '#03AF9A'}}>
                                    ✓ Request logged — the PbDQ team will contact your registered official email address.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {!loading && activeTab === 1 && (
                    <>
                        <Typography component="h2" sx={sectionH2Sx}>Stakeholder Scores</Typography>
                        <Typography component="p" sx={sectionPSx}>
                            The CPC Index assesses national child online protection across six stakeholder groups.
                        </Typography>
                        <Typography component="p" sx={sectionPSx}>
                            <Box component="span" sx={{fontWeight: 700, color: PURPLE}}>Stakeholder 1 – Competent Children</Box> is measured
                            through the Children&rsquo;s Assessment Toolkit, which evaluates children&rsquo;s cyber
                            citizenship competencies and cyber risk outcomes.
                        </Typography>
                        <Typography component="p" sx={sectionPSx}>
                            <Box component="span" sx={{fontWeight: 700, color: C.text}}>Stakeholders 2–6</Box> are measured through the Government
                            Checklist Toolkit, which evaluates the national systems, policies, services, infrastructure,
                            and governance established to support children, families, schools, companies, technology
                            infrastructure, and government.
                        </Typography>

                        {notice && (
                            <Box sx={{background: '#fdf6f2', border: '1px solid rgba(192,85,58,.28)', color: '#a3452d', padding: '12px 16px', fontSize: 14, fontWeight: 600, mb: 2.5}}>
                                {notice}
                            </Box>
                        )}

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)'}, gap: 2, margin: '24px 0 48px'}}>
                            {stakeholderCards.map((s) => {
                                const t = s.tierStyle;
                                const isCA = s.id === 1;
                                const headerBg = isCA ? PURPLE : C.navy;
                                return (
                                    <Box
                                        key={s.id}
                                        onClick={() => {
                                           if (isCA) {
                                                setNotice(`${s.name} has no Government Checklist indicators to review.`);
                                                return;
                                            }
                                            setNotice('');
                                            history.push(`/dq_pro/cpc-government-dashboard/explorer?stakeholder=${s.id}`, {fromOverview: true});
                                        }}
                                        sx={{
                                            border: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                                            background: C.surface,
                                            cursor: 'pointer',
                                            transition: 'box-shadow .15s ease',
                                            '&:hover': {boxShadow: '0 6px 18px rgba(0,40,85,.1)'},
                                        }}
                                    >
                                        <Box sx={{background: headerBg, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 1.25}}>
                                            <Box
                                                component="span"
                                                aria-hidden
                                                sx={{
                                                    flex: 'none',
                                                    width: 26,
                                                    height: 26,
                                                    border: '2px solid rgba(255,255,255,.85)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: C.white,
                                                }}
                                            >
                                                S{s.id}
                                            </Box>
                                            <Typography sx={{fontSize: 16, fontWeight: 700, color: C.white, lineHeight: 1.3}}>{s.name}</Typography>
                                        </Box>

                                        <Box sx={{padding: '18px 20px'}}>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-block',
                                                    fontSize: 11.5,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.05em',
                                                    textTransform: 'uppercase',
                                                    padding: '5px 10px',
                                                    mb: 1.5,
                                                    border: `1px solid ${t.fg}`,
                                                    color: t.fg,
                                                }}
                                            >
                                                {t.label}
                                            </Box>

                                            <Typography sx={{fontSize: 14, color: C.muted, lineHeight: 1.45, mb: 2}}>{s.toolkit}</Typography>

                                            <Box sx={{display: 'flex', mb: 1.75}}>
                                                {[
                                                    {label: 'Met', n: s.met, color: EMERALD},
                                                    {label: 'Partially', n: s.partial, color: AMBER},
                                                    {label: 'Not Met', n: s.notMet, color: CORAL},
                                                ].map((st, i) => (
                                                    <Box
                                                        key={st.label}
                                                        sx={{
                                                            flex: '1 1 0%',
                                                            textAlign: 'center',
                                                            padding: '0 6px',
                                                            borderRight: i < 2 ? '1px solid var(--cpc-border, rgba(0,60,105,.12))' : 'none',
                                                        }}
                                                    >
                                                        <Typography sx={{fontSize: 20, fontWeight: 700, color: C.text, lineHeight: 1}}>{st.n}</Typography>
                                                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5}}>
                                                            <Box component="span" sx={{width: 8, height: 8, flex: 'none', background: st.color}}/>
                                                            <Typography sx={{fontSize: 11, color: C.muted}}>{st.label}</Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>

                                            <Typography sx={{fontSize: 13.5, color: C.muted}}>
                                                {s.indicators} indicators
                                                {s.confidence && ` · ${s.confidence} confidence`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}

                {!loading && activeTab === 2 && (
                    <>
                        <Typography component="h2" sx={sectionH2Sx}>Target Scores</Typography>
                        <Typography component="p" sx={sectionPSx}>
                            Each stakeholder score is derived from three target scores, resulting in a total of 18 CPC
                            Index targets.
                        </Typography>
                        <Typography component="p" sx={sectionPSx}>
                            <Box component="span" sx={{fontWeight: 700, color: PURPLE}}>Targets 1–3</Box> are measured through the CPC Index
                            Children&rsquo;s Assessment Toolkit, which assesses children&rsquo;s cyber citizenship
                            competencies and cyber risk outcomes.
                        </Typography>
                        <Typography component="p" sx={sectionPSx}>
                            <Box component="span" sx={{fontWeight: 700, color: C.text}}>Targets 4–18</Box> are measured through the CPC Index
                            Government Checklist Toolkit, which assesses government capabilities and national system
                            performance across families and communities, schools, companies, technology infrastructure,
                            and government.
                        </Typography>

                        {notice && (
                            <Box sx={{background: '#fdf6f2', border: '1px solid rgba(192,85,58,.28)', color: '#a3452d', padding: '12px 16px', fontSize: 14, fontWeight: 600, mb: 2.5}}>
                                {notice}
                            </Box>
                        )}

                        <Box sx={{border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', background: C.surface, margin: '24px 0 40px'}}>
                            {STAKEHOLDERS.map((stakeholder, groupIdx) => {
                                const isCA = stakeholder.id === 1;
                                const groupAccent = isCA ? PURPLE : C.navy;
                                const rows = targetCards.filter((t) => t.s === stakeholder.id);
                                return (
                                    <Box key={stakeholder.id}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 1.25,
                                                flexWrap: 'wrap',
                                                padding: '12px 18px',
                                                background: C.mist,
                                                boxShadow: `inset 4px 0 0 ${groupAccent}`,
                                                borderTop: groupIdx > 0 ? '1px solid var(--cpc-border, rgba(0,60,105,.12))' : 'none',
                                                borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                                            }}
                                        >
                                            <Typography sx={{fontSize: 15, fontWeight: 700, color: C.text}}>
                                                S{stakeholder.id} · {stakeholder.name}
                                            </Typography>
                                            <Typography sx={{fontSize: 12.5, color: C.muted}}>{stakeholder.toolkit}</Typography>
                                        </Box>

                                        {rows.map((t, rowIdx) => {
                                            const tt = t.tierStyle;
                                            return (
                                                <Box
                                                    key={t.n}
                                                    onClick={() => {
                                                        // Same reasoning as the stakeholder card above:
                                                        // Targets 1–3 belong to the Children's Assessment
                                                        // stakeholder and have no Government Checklist
                                                        // indicators to filter to.
                                                        if (isCA) {
                                                            setNotice(`${t.name} is assessed through the Children’s Assessment Toolkit and has no Government Checklist indicators to review here.`);
                                                            return;
                                                        }
                                                        setNotice('');
                                                        history.push(`/dq_pro/cpc-government-dashboard/explorer?target=${t.n}`, {fromOverview: true});
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        padding: '13px 18px',
                                                        cursor: 'pointer',
                                                        borderBottom: rowIdx < rows.length - 1 ? '1px solid var(--cpc-border, rgba(0,60,105,.08))' : 'none',
                                                        transition: 'background .15s ease',
                                                        '&:hover': {background: C.mist},
                                                        '@media (max-width: 720px)': {flexWrap: 'wrap', gap: 1.25},
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        aria-hidden
                                                        sx={{
                                                            flex: 'none',
                                                            width: 28,
                                                            height: 28,
                                                            border: `2px solid ${C.text}`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: 11.5,
                                                            fontWeight: 700,
                                                            color: C.text,
                                                        }}
                                                    >
                                                        {String(t.n).padStart(2, '0')}
                                                    </Box>

                                                    <Typography sx={{flex: '1 1 260px', fontSize: 14.5, fontWeight: 700, color: C.text, lineHeight: 1.3}}>
                                                        {t.name}
                                                    </Typography>

                                                    <Box sx={{flex: 'none', display: 'flex', gap: 2}}>
                                                        {[
                                                            {label: 'Met', n: t.met, color: EMERALD},
                                                            {label: 'Partially', n: t.partial, color: AMBER},
                                                            {label: 'Not Met', n: t.notMet, color: CORAL},
                                                        ].map((st) => (
                                                            <Box key={st.label} sx={{textAlign: 'center', minWidth: 58}}>
                                                                <Typography sx={{fontSize: 15, fontWeight: 700, color: st.color, lineHeight: 1}}>{st.n}</Typography>
                                                                <Typography sx={{fontSize: 10.5, color: C.muted, mt: 0.4}}>{st.label}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>

                                                    <Box sx={{flex: 'none', minWidth: 118, textAlign: 'right'}}>
                                                        <Typography sx={{fontSize: 13, fontWeight: 700, color: tt.fg}}>{tt.label}</Typography>
                                                        {t.confidence && (
                                                            <Typography sx={{fontSize: 11, color: C.muted, mt: 0.25}}>{t.confidence} confidence</Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5}}>
                            <Tooltip title="Review and act on all 85 checklist indicators" arrow>
                                <Button onClick={() => history.push('/dq_pro/cpc-government-dashboard/explorer')} sx={ctaSx(C.navy, C.navy, C.white)}>
                                    Open Explorer
                                </Button>
                            </Tooltip>
                            <Tooltip title="See how much of the checklist has been reviewed" arrow>
                                <Button onClick={() => history.push('/dq_pro/cpc-government-dashboard/progress')} sx={ctaSx(C.surface, 'var(--cpc-border, rgba(0,60,105,.25))', C.text)}>
                                    Open Review Progress
                                </Button>
                            </Tooltip>
                        </Box>
                    </>
                )}
            </Box>
        </DashboardShell>
    );
};

const NationalOverviewPageWithProvider = () => (
    <DashboardProvider>
        <NationalOverviewPage/>
    </DashboardProvider>
);

export default NationalOverviewPageWithProvider;
